import React from "react"
import { Popover, Tag, Tooltip } from "antd"
import { PLANNING_STATUS } from "services/leaveService"

/**
 * One description of every state a leave plan can be in, for every screen that shows one.
 *
 * The server's names describe what the system did (submitted, coordinated, converted); a
 * newcomer reads them as what they mean for their leave, and "submitted" and "converted" both
 * sound like something was booked. The labels here answer the question people actually have —
 * is this booked or not — and the explanations say what the state means and what can happen
 * next, worded for whoever is looking: the employee who planned it, or the supervisor
 * coordinating the roster.
 */

export type PlanAudience = "employee" | "supervisor"

interface PlanStatusMeta {
  label: string
  colour: string
  meaning: Record<PlanAudience, string>
  next: Record<PlanAudience, string | null>
}

export const PLAN_STATUS_META: Record<number, PlanStatusMeta> = {
  [PLANNING_STATUS.DRAFT]: {
    label: "Draft",
    colour: "default",
    meaning: {
      employee:
        "Only you can see this. It is not on your department's roster yet.",
      supervisor:
        "Only the employee can see a draft, so it never appears on the roster.",
    },
    next: {
      employee:
        "Share it when you are happy with the dates, so your supervisor can plan around it.",
      supervisor: null,
    },
  },
  [PLANNING_STATUS.SUBMITTED]: {
    label: "Shared",
    colour: "blue",
    meaning: {
      employee:
        "Your supervisor can see it on the roster. It is still only a plan: nothing is booked and no days are used.",
      supervisor:
        "The employee has shared these dates. Nothing is booked and no days have been used.",
    },
    next: {
      employee:
        "Your supervisor may agree it or ask for changes. You can still edit it until then.",
      supervisor: "Agree it, or ask the employee to change it with a comment.",
    },
  },
  [PLANNING_STATUS.REVISION_REQUESTED]: {
    label: "Changes requested",
    colour: "orange",
    meaning: {
      employee:
        "Your supervisor has asked you to change these dates. Their comment says why.",
      supervisor:
        "You asked the employee to change these dates. It stays on the roster while they do.",
    },
    next: {
      employee: "Edit the dates to share it again, or remove it.",
      supervisor: "Wait for the employee to update it, then agree it.",
    },
  },
  [PLANNING_STATUS.COORDINATED]: {
    label: "Agreed",
    colour: "green",
    meaning: {
      employee:
        "Your supervisor has agreed these dates with the department. You can no longer change them. It is still not booked.",
      supervisor:
        "Agreed with the department. The employee can no longer change the dates, and nothing is booked yet.",
    },
    next: {
      employee:
        "When the time comes, request this leave to book it through normal approval.",
      supervisor: "The employee books it by turning it into a leave request.",
    },
  },
  [PLANNING_STATUS.CONVERTED]: {
    label: "Booked as leave",
    colour: "purple",
    meaning: {
      employee:
        "This plan has become a real leave request. It follows the normal approval process and uses your balance.",
      supervisor:
        "This plan has become a real leave request, handled through normal approval with its days against the balance.",
    },
    next: {
      employee: "Follow it under Leave requests.",
      supervisor: "Review it with the other leave requests.",
    },
  },
  [PLANNING_STATUS.WITHDRAWN]: {
    label: "Withdrawn",
    colour: "default",
    meaning: {
      employee:
        "You removed this from your plan. It is kept for reporting only.",
      supervisor:
        "The employee removed this from their plan. It is kept for reporting only.",
    },
    next: { employee: null, supervisor: null },
  },
}

/** The label for a status, falling back to the server's name for anything unknown. */
export const planStatusLabel = (
  statusId?: number | null,
  serverName?: string | null
) => PLAN_STATUS_META[statusId ?? 0]?.label ?? serverName ?? "Planned"

/** What the state means and what happens next, as one block for a tooltip. */
const PlanStatusExplanation: React.FC<{
  statusId: number
  audience: PlanAudience
}> = ({ statusId, audience }) => {
  const meta = PLAN_STATUS_META[statusId]
  if (!meta) return null
  const next = meta.next[audience]

  return (
    <div className="flex flex-col gap-1.5 font-avenir text-xs leading-relaxed">
      <span className="font-semibold">{meta.label}</span>
      <span>{meta.meaning[audience]}</span>
      {next ? <span className="opacity-80">Next: {next}</span> : null}
    </div>
  )
}

/** A plan's status as a tag that explains itself on hover or keyboard focus. */
export const LeavePlanStatusTag: React.FC<{
  statusId?: number | null
  serverName?: string | null
  audience: PlanAudience
}> = ({ statusId, serverName, audience }) => {
  const meta = PLAN_STATUS_META[statusId ?? 0]

  const tag = (
    <Tag
      color={meta?.colour ?? "default"}
      className="m-0 cursor-help"
      tabIndex={0}
    >
      {planStatusLabel(statusId, serverName)}
    </Tag>
  )

  return meta ? (
    <Tooltip
      title={
        <PlanStatusExplanation
          statusId={statusId as number}
          audience={audience}
        />
      }
      overlayStyle={{ maxWidth: 320 }}
    >
      {tag}
    </Tooltip>
  ) : (
    tag
  )
}

interface FlowStepCopy {
  statusId: number
  text: string
}

/** The main path, in order, told as what each person does at that step. */
const FLOW_STEPS: Record<PlanAudience, FlowStepCopy[]> = {
  employee: [
    {
      statusId: PLANNING_STATUS.DRAFT,
      text: "Add the dates you have in mind. Only you can see them.",
    },
    {
      statusId: PLANNING_STATUS.SUBMITTED,
      text: "Share it so your supervisor can plan the team around it.",
    },
    {
      statusId: PLANNING_STATUS.COORDINATED,
      text: "Your supervisor agrees the dates with your department.",
    },
    {
      statusId: PLANNING_STATUS.CONVERTED,
      text: "When it is time, request the leave. It goes for approval and uses your balance.",
    },
  ],
  supervisor: [
    {
      statusId: PLANNING_STATUS.SUBMITTED,
      text: "An employee shares the dates they plan to take.",
    },
    {
      statusId: PLANNING_STATUS.COORDINATED,
      text: "You agree the dates with the department.",
    },
    {
      statusId: PLANNING_STATUS.CONVERTED,
      text: "The employee requests the leave, and it goes through normal approval.",
    },
  ],
}

/** The ways off the main path. */
const FLOW_EXCEPTIONS: Record<PlanAudience, FlowStepCopy[]> = {
  employee: [
    {
      statusId: PLANNING_STATUS.REVISION_REQUESTED,
      text: "Your supervisor can send a shared plan back with a comment. Change the dates and share it again.",
    },
    {
      statusId: PLANNING_STATUS.WITHDRAWN,
      text: "You can remove a plan any time before it is agreed.",
    },
  ],
  supervisor: [
    {
      statusId: PLANNING_STATUS.REVISION_REQUESTED,
      text: "Send a shared plan back with a comment. The employee changes the dates and shares it again.",
    },
    {
      statusId: PLANNING_STATUS.WITHDRAWN,
      text: "The employee can remove a plan any time before it is agreed.",
    },
  ],
}

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <span
    aria-hidden="true"
    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary500 font-circular text-xs font-semibold text-white"
  >
    {n}
  </span>
)

const NothingBooked: React.FC<{ audience: PlanAudience }> = ({ audience }) => (
  <div className="flex items-start gap-2 rounded-md bg-[#EEF4FF] px-3 py-2.5 font-avenir text-[13px] leading-snug text-padeBlack">
    <span
      aria-hidden="true"
      className="mt-[1px] text-primary500"
    >
      ●
    </span>
    <span>
      {audience === "employee"
        ? "Nothing is booked and no days are used until you request the leave. Planning is for leave you know about ahead of time; for anything else, submit a leave request."
        : "Nothing is booked and no days are used until the employee requests the leave."}
    </span>
  </div>
)

const Exceptions: React.FC<{ audience: PlanAudience; columns: boolean }> = ({
  audience,
  columns,
}) => (
  <div
    className={`grid gap-3 ${
      columns ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
    }`}
  >
    {FLOW_EXCEPTIONS[audience].map((item) => (
      <div
        key={item.statusId}
        className="flex flex-col items-start gap-1.5 rounded-md border border-neutral40 bg-white px-3 py-2.5"
      >
        <LeavePlanStatusTag
          statusId={item.statusId}
          audience={audience}
        />
        <span className="font-avenir text-[13px] leading-snug text-neutral400">
          {item.text}
        </span>
      </div>
    ))}
  </div>
)

/**
 * How a plan moves, drawn before anyone has made one, so the states are not first met as
 * unexplained chips in a table.
 *
 * Two arrangements of the same content. `panel` is for the page: the main path as numbered
 * columns joined by a line, the exceptions side by side beneath. `compact` is for the popover,
 * where columns would be too narrow to read: the same steps down a numbered line.
 */
export const LeavePlanLifecycle: React.FC<{
  audience: PlanAudience
  variant?: "panel" | "compact"
  className?: string
}> = ({ audience, variant = "panel", className = "" }) => {
  const steps = FLOW_STEPS[audience]

  if (variant === "compact") {
    return (
      <div
        className={`flex flex-col gap-4 ${className}`}
        aria-label="How a leave plan moves"
      >
        <ol className="m-0 flex list-none flex-col p-0">
          {steps.map((step, index) => (
            <li
              key={step.statusId}
              className="relative flex gap-3 pb-4 last:pb-0"
            >
              {index < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute left-3 top-6 bottom-0 w-px bg-neutral40"
                />
              ) : null}
              <StepNumber n={index + 1} />
              <div className="flex flex-col items-start gap-1 pt-0.5">
                <LeavePlanStatusTag
                  statusId={step.statusId}
                  audience={audience}
                />
                <span className="font-avenir text-[13px] leading-snug text-neutral400">
                  {step.text}
                </span>
              </div>
            </li>
          ))}
        </ol>
        <div className="flex flex-col gap-2">
          <span className="font-circular text-xs font-semibold uppercase tracking-wide text-padeSubtext">
            Also
          </span>
          <Exceptions
            audience={audience}
            columns={false}
          />
        </div>
        <NothingBooked audience={audience} />
      </div>
    )
  }

  return (
    <div
      className={`flex flex-col gap-5 ${className}`}
      aria-label="How a leave plan moves"
    >
      <ol
        className={`m-0 grid list-none grid-cols-1 gap-4 p-0 ${
          steps.length === 4 ? "md:grid-cols-4" : "md:grid-cols-3"
        }`}
      >
        {steps.map((step, index) => (
          <li
            key={step.statusId}
            className="relative flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <StepNumber n={index + 1} />
              {/* The line to the next step, drawn on wide screens where steps sit in a row. */}
              {index < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="hidden h-px flex-1 bg-neutral40 md:block"
                />
              ) : null}
            </div>
            <div className="flex flex-col items-start gap-1.5 md:pr-4">
              <LeavePlanStatusTag
                statusId={step.statusId}
                audience={audience}
              />
              <span className="font-avenir text-[13px] leading-snug text-neutral400">
                {step.text}
              </span>
            </div>
          </li>
        ))}
      </ol>

      <div className="flex flex-col gap-2">
        <span className="font-circular text-xs font-semibold uppercase tracking-wide text-padeSubtext">
          Along the way
        </span>
        <Exceptions
          audience={audience}
          columns
        />
      </div>

      <NothingBooked audience={audience} />
    </div>
  )
}

/**
 * "How planning works" as a link that opens the flow on a light surface. The flow holds status
 * chips of its own, which a dark tooltip would render unreadable.
 */
export const LeavePlanFlowLink: React.FC<{ audience: PlanAudience }> = ({
  audience,
}) => (
  <Popover
    trigger={["hover", "click"]}
    placement="bottomLeft"
    title={
      <span className="font-circular text-sm font-semibold text-padeBlack">
        How leave planning works
      </span>
    }
    content={
      <div className="w-[340px] max-w-[calc(100vw-48px)] pt-1">
        <LeavePlanLifecycle
          audience={audience}
          variant="compact"
        />
      </div>
    }
  >
    <button
      type="button"
      className="inline-flex items-center gap-1 font-avenir text-xs font-normal text-primary500 hover:text-primary700"
    >
      <span
        aria-hidden="true"
        className="flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px] leading-none"
      >
        ?
      </span>
      How planning works
    </button>
  </Popover>
)
